import { Injectable, inject } from '@angular/core';
import { Observable, of, forkJoin } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ProfileService } from '@outlaw/core';
import { CommunityItem, ShortLikePost, CommentsBulk, Selection, PostExtendOptions } from '../../interfaces';
import { PostService } from '../api/post.service';
import { CommentService } from '../api/comment.service';
import { SelectionService } from '../api/selection.service';
import { SelectionStateService } from './selection-state.service';

@Injectable({ providedIn: 'root' })
export class PostExtenderService {
  #postService = inject(PostService);
  #commentService = inject(CommentService);
  #selectionService = inject(SelectionService);
  #selectionStateService = inject(SelectionStateService);
  #profileService = inject(ProfileService);

  extendPosts(posts: CommunityItem[], options?: PostExtendOptions): Observable<CommunityItem[]> {
    if (posts.length === 0) return of([]);

    const postIDs = posts.map(item => item.post_uuid);

    const likes$ = options?.skipLikes
      ? of({} as ShortLikePost)
      : this.#postService.getMyFollowingsLikedPosts(postIDs).pipe(catchError(() => of({} as ShortLikePost)));

    const comments$ = options?.skipComments
      ? of({} as CommentsBulk)
      : this.#commentService.bulkCommentsCount(postIDs).pipe(catchError(() => of({} as CommentsBulk)));

    const currentUserUuid = this.#profileService.getUser()?.passport_uuid;
    const selections$ = options?.skipSelections
      ? of([] as Selection[])
      : currentUserUuid
        ? this.#selectionService.getSelections(currentUserUuid).pipe(catchError(() => of([] as Selection[])))
        : of([] as Selection[]);

    return forkJoin({ likes: likes$, comments: comments$, selections: selections$ }).pipe(
      map(({ likes, comments, selections }) => {
        if (!options?.skipSelections) {
          this.#selectionStateService.setSelections(selections);
        }

        const selMap = new Map<string, boolean>();
        selections.forEach(sel =>
          sel.posts_uuids.forEach(uuid => selMap.set(uuid, true))
        );

        return posts.map(item => ({
          ...item,
          liked_by: likes[item.post_uuid] || [],
          comments_count: comments[item.post_uuid]?.count || 0,
          is_saved: selMap.has(item.post_uuid),
        }));
      })
    );
  }
}
